import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Tag, 
  Button, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  Row, 
  Col, 
  Statistic, 
  Modal, 
  Descriptions, 
  message,
  notification,
  Form,
  Typography
} from 'antd';
import { 
  SearchOutlined, 
  ExportOutlined, 
  EyeOutlined, 
  UserOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileTextOutlined,
  TruckOutlined,
  CreditCardOutlined,
  ShoppingOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import './AdminPedidos.css';
import * as XLSX from 'xlsx';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { buildApiUrl } from '../utils/api';

const { Title, Paragraph } = Typography;

const AdminPedidos = () => {
  const [pedidos, setPedidos] = useState([]);
  const [selectedPedido, setSelectedPedido] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [form] = Form.useForm();
  const [filtrosAplicados, setFiltrosAplicados] = useState({});
  const [valoresFiltros, setValoresFiltros] = useState({});
  const [filtrosAvançadosAbertos, setFiltrosAvançadosAbertos] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  
  // Estados para o modal de cancelamento
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pedidoParaCancelar, setPedidoParaCancelar] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  useEffect(() => {
    fetchPedidos();
  }, []);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.custom-dropdown-container')) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const fetchPedidos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/admin/pedidos'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Se os pedidos não tiverem itens, buscar os detalhes completos de cada um
        if (data.pedidos && data.pedidos.length > 0) {
          const pedidosCompletos = [];
          
          for (const pedido of data.pedidos) {
            try {
              // Verificar se o pedido já tem itens
              const temItens = (pedido.itens && Array.isArray(pedido.itens) && pedido.itens.length > 0) ||
                              (pedido.items && Array.isArray(pedido.items) && pedido.items.length > 0) ||
                              (pedido.itens && typeof pedido.itens === 'object' && Object.keys(pedido.itens).length > 0) ||
                              (pedido.items && typeof pedido.items === 'object' && Object.keys(pedido.items).length > 0) ||
                              (typeof pedido.itens === 'string' && pedido.itens.trim() !== '') ||
                              (typeof pedido.items === 'string' && pedido.items.trim() !== '');
              
              if (!temItens) {
                const detalhesResponse = await fetch(buildApiUrl(`/api/admin/pedidos/${pedido.id}`), {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                
                if (detalhesResponse.ok) {
                  const detalhesData = await detalhesResponse.json();
                  pedidosCompletos.push(detalhesData.pedido);
                } else {
                  pedidosCompletos.push(pedido);
                }
              } else {
                pedidosCompletos.push(pedido);
              }
            } catch (error) {
              console.error(`Erro ao buscar detalhes do pedido ${pedido.id}:`, error);
              pedidosCompletos.push(pedido);
            }
          }
          
          setPedidos(pedidosCompletos);
        } else {
          setPedidos([]);
        }
      } else {
        console.error('Erro ao buscar pedidos');
      }
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
    }
  };

  const handleViewPedido = async (pedidoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl(`/api/admin/pedidos/${pedidoId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedPedido(data.pedido);
        setShowModal(true);
      } else {
        console.error('Erro ao buscar detalhes do pedido');
      }
    } catch (error) {
      console.error('Erro ao buscar detalhes do pedido:', error);
    }
  };

  const handleStatusChange = async (pedidoId, newStatus) => {
    if (newStatus === 'Entregue') {
      const confirmacao = window.confirm('Tem certeza que deseja marcar este pedido como "Entregue"? Esta ação não poderá ser desfeita.');
      if (!confirmacao) return;
    }

    // Se o status for Cancelado, abrir modal para motivo
    if (newStatus === 'Cancelado') {
      const pedido = pedidos.find(p => p.id === pedidoId);
      setPedidoParaCancelar(pedido);
      setShowCancelModal(true);
      return;
    }

    // Mapear o status visual para o status do backend
    let statusBackend = newStatus;
    if (newStatus === 'Em Transporte') {
      statusBackend = 'Enviado';
    }

    await atualizarStatusPedido(pedidoId, statusBackend);
  };

  const atualizarStatusPedido = async (pedidoId, statusBackend, motivoCancelamento = null) => {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const body = { status: statusBackend };
      
      // Se for cancelamento, incluir o motivo
      if (statusBackend === 'Cancelado' && motivoCancelamento) {
        body.motivo_cancelamento = motivoCancelamento;
      }

      const response = await fetch(buildApiUrl(`/api/admin/pedidos/${pedidoId}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        // Atualizar a lista de pedidos com o status visual
        setPedidos(prevPedidos => 
          prevPedidos.map(pedido => 
            pedido.id === pedidoId 
              ? { 
                  ...pedido, 
                  status: getStatusVisual(statusBackend),
                  motivo_cancelamento: motivoCancelamento || pedido.motivo_cancelamento
                }
              : pedido
          )
        );

        // Se o modal estiver aberto, atualizar o pedido selecionado
        if (selectedPedido && selectedPedido.id === pedidoId) {
          setSelectedPedido(prev => ({ 
            ...prev, 
            status: getStatusVisual(statusBackend),
            motivo_cancelamento: motivoCancelamento || prev.motivo_cancelamento
          }));
        }

        alert('Status do pedido atualizado com sucesso!');
      } else {
        const errorData = await response.json();
        alert(`Erro ao atualizar status: ${errorData.message}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status do pedido');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancelarPedido = async () => {
    if (!motivoCancelamento.trim()) {
      alert('Por favor, informe o motivo do cancelamento.');
      return;
    }

    await atualizarStatusPedido(pedidoParaCancelar.id, 'Cancelado', motivoCancelamento);
    
    // Fechar modal e limpar estados
    setShowCancelModal(false);
    setPedidoParaCancelar(null);
    setMotivoCancelamento('');
  };

  // Função para mapear o status do backend para o status visual
  const getStatusVisual = (status) => {
    if (status === 'Enviado') {
      return 'Em Transporte';
    }
    return status;
  };

  const getStatusColor = (status) => {
    // Normalizar o status para comparação
    const normalizedStatus = status.toLowerCase();
    
    switch (normalizedStatus) {
      case 'pendente de aprovação':
      case 'pendente':
        return '#ffc107'; // Amarelo
      case 'aprovado':
        return '#28a745'; // Verde
      case 'em transporte':
      case 'em preparação':
      case 'enviado':
        return '#2196f3'; // Azul
      case 'entregue':
        return '#4caf50'; // Verde
      case 'cancelado':
        return '#f44336'; // Vermelho
      default:
        return '#757575';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'R$ 0,00';
    }
    
    const valorNumerico = parseFloat(value);
    
    if (isNaN(valorNumerico)) {
      return 'R$ 0,00';
    }
    
    // Formatação manual para garantir padrão brasileiro
    const parteInteira = Math.floor(valorNumerico);
    const parteDecimal = Math.round((valorNumerico - parteInteira) * 100);
    
    // Formatar parte inteira com pontos para milhares
    const parteInteiraFormatada = parteInteira.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Formatar parte decimal sempre com 2 dígitos
    const parteDecimalFormatada = parteDecimal.toString().padStart(2, '0');
    
    return `R$ ${parteInteiraFormatada},${parteDecimalFormatada}`;
  };

  // Função para filtrar pedidos
  const pedidosFiltrados = pedidos.filter(pedido => {
    // Filtro por ID - busca exata
    if (filtrosAplicados.id && pedido.id.toString() !== filtrosAplicados.id) {
      return false;
    }
    
    // Filtro por range de ID
    if (filtrosAplicados.idInicial || filtrosAplicados.idFinal) {
      const idPedido = parseInt(pedido.id);
      const idInicial = filtrosAplicados.idInicial ? parseInt(filtrosAplicados.idInicial) : 0;
      const idFinal = filtrosAplicados.idFinal ? parseInt(filtrosAplicados.idFinal) : Number.MAX_SAFE_INTEGER;
      
      if (idPedido < idInicial || idPedido > idFinal) {
        return false;
      }
    }
    
    // Filtro por cliente
    if (filtrosAplicados.cliente && !(pedido.user?.username?.toLowerCase() || '').includes(filtrosAplicados.cliente.toLowerCase())) {
      return false;
    }
    
    // Filtro por status
    if (filtrosAplicados.status) {
      if (filtrosAplicados.status === 'Em Transporte') {
        // Se o filtro for "Em Transporte", mostrar "Em Preparação", "Em Transporte" e "Enviado"
        if (pedido.status !== 'Em Preparação' && pedido.status !== 'Em Transporte' && pedido.status !== 'Enviado') {
          return false;
        }
      } else if (pedido.status !== filtrosAplicados.status) {
        return false;
      }
    }
    
    // Filtro por data
    if (filtrosAplicados.dataInicio || filtrosAplicados.dataFim) {
      const dataPedido = new Date(pedido.created_at);
      
      if (filtrosAplicados.dataInicio) {
        const dataInicio = filtrosAplicados.dataInicio.startOf('day').toDate();
        if (dataPedido < dataInicio) {
          return false;
        }
      }
      
      if (filtrosAplicados.dataFim) {
        const dataFim = filtrosAplicados.dataFim.endOf('day').toDate();
        if (dataPedido > dataFim) {
          return false;
        }
      }
    }
    
    return true;
  });

  // Função para aplicar filtros
  const aplicarFiltros = () => {
    setFiltrosAplicados({...valoresFiltros});
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    form.resetFields();
    setFiltrosAplicados({});
    setValoresFiltros({});
    setFiltrosAvançadosAbertos(false);
  };

  // Função para alternar filtros avançados
  const toggleFiltrosAvançados = () => {
    setFiltrosAvançadosAbertos(!filtrosAvançadosAbertos);
  };

  // Função para exportar pedidos para Excel
  const exportarParaExcel = () => {
    if (pedidosFiltrados.length === 0) {
      message.warning('Não há pedidos para exportar!');
      return;
    }

    Modal.confirm({
      title: 'Confirmar Exportação',
      content: (
        <Space direction="vertical" size="middle">
          <p>Deseja exportar <strong>{pedidosFiltrados.length} pedido(s)</strong> para Excel?</p>
          <p>O arquivo incluirá todos os dados dos pedidos, clientes, endereços, itens detalhados e informações de logo extra.</p>
        </Space>
      ),
      okText: 'Exportar',
      cancelText: 'Cancelar',
      onOk: () => {
        setExportando(true);
        realizarExportacao();
      }
    });
  };

  const realizarExportacao = () => {
    try {
      // Preparar dados para exportação - Resumo dos pedidos
      const dadosParaExportar = pedidosFiltrados.map(pedido => {
        // Dados básicos do pedido
        const dadosBasicos = {
          'ID do Pedido': pedido.id,
          'Status': getStatusVisual(pedido.status),
          'Data de Criação': formatDate(pedido.created_at),
          'Total': formatCurrency(pedido.total),
          'Observações': pedido.observacoes || '',
          'Logo Extra - Produtos': pedido.logo_extra?.produtos || pedido.logo_extra_produtos || 'Não solicitado',
          'Logo Extra - Entende Alteração de Valor': pedido.logo_extra?.entende_alteracao_valor ? 'Sim' : 'Não'
        };

        // Dados do cliente
        const dadosCliente = {
          'Nome do Cliente': pedido.user?.username || pedido.dados_pessoais?.nome_completo || 'Não informado',
          'Email': pedido.user?.email || 'Não informado',
          'CPF/CNPJ': pedido.dados_pessoais?.cpf || pedido.dados_pessoais?.cnpj || 'Não informado',
          'Empresa': pedido.dados_pessoais?.nome_empresa || 'Não informado',
          'Telefone': pedido.dados_pessoais?.telefone || 'Não informado'
        };

        // Dados de entrega
        const dadosEntrega = {
          'Quem Receberá': pedido.dados_entrega?.quem_recebera || 'Não informado',
          'Rua': pedido.dados_entrega?.rua || 'Não informado',
          'Número': pedido.dados_entrega?.numero || 'Não informado',
          'Complemento': pedido.dados_entrega?.complemento || 'Não informado',
          'Bairro': pedido.dados_entrega?.bairro || 'Não informado',
          'Cidade': pedido.dados_entrega?.cidade || 'Não informado',
          'Estado': pedido.dados_entrega?.estado || 'Não informado',
          'CEP': pedido.dados_entrega?.cep || 'Não informado',
          'Ponto de Referência': pedido.dados_entrega?.ponto_referencia || 'Não informado'
        };

        // Dados de faturamento (se diferente da entrega)
        const dadosFaturamento = {};
        if (pedido.dados_faturamento && !pedido.endereco_entrega_igual_faturamento) {
          Object.assign(dadosFaturamento, {
            'Rua Faturamento': pedido.dados_faturamento.rua || 'Não informado',
            'Número Faturamento': pedido.dados_faturamento.numero || 'Não informado',
            'Complemento Faturamento': pedido.dados_faturamento.complemento || 'Não informado',
            'Bairro Faturamento': pedido.dados_faturamento.bairro || 'Não informado',
            'Cidade Faturamento': pedido.dados_faturamento.cidade || 'Não informado',
            'Estado Faturamento': pedido.dados_faturamento.estado || 'Não informado',
            'CEP Faturamento': pedido.dados_faturamento.cep || 'Não informado',
            'Ponto de Referência Faturamento': pedido.dados_faturamento.ponto_referencia || 'Não informado'
          });
        } else {
          Object.assign(dadosFaturamento, {
            'Endereço Faturamento': 'Igual ao de entrega'
          });
        }

        // Combinar todos os dados
        return {
          ...dadosBasicos,
          ...dadosCliente,
          ...dadosEntrega,
          ...dadosFaturamento
        };
      });

      // Preparar dados dos itens detalhados
      const itensDetalhados = [];
      pedidosFiltrados.forEach(pedido => {
        // Tentar diferentes formas de acessar os itens
        let itens = [];
        if (Array.isArray(pedido.itens)) {
          itens = pedido.itens;
        } else if (Array.isArray(pedido.items)) {
          itens = pedido.items;
        } else if (pedido.itens && typeof pedido.itens === 'object') {
          // Se itens for um objeto, tentar extrair array
          itens = Object.values(pedido.itens);
        } else if (pedido.items && typeof pedido.items === 'object') {
          // Se items for um objeto, tentar extrair array
          itens = Object.values(pedido.items);
        } else if (typeof pedido.itens === 'string') {
          // Se itens for uma string, tentar fazer parse JSON
          try {
            const parsedItens = JSON.parse(pedido.itens);
            if (Array.isArray(parsedItens)) {
              itens = parsedItens;
            } else if (typeof parsedItens === 'object') {
              itens = Object.values(parsedItens);
            }
          } catch (e) {
            console.log(`Erro ao fazer parse JSON dos itens do pedido ${pedido.id}:`, e);
          }
        } else if (typeof pedido.items === 'string') {
          // Se items for uma string, tentar fazer parse JSON
          try {
            const parsedItems = JSON.parse(pedido.items);
            if (Array.isArray(parsedItems)) {
              itens = parsedItems;
            } else if (typeof parsedItems === 'object') {
              itens = Object.values(parsedItems);
            }
          } catch (e) {
            console.log(`Erro ao fazer parse JSON dos items do pedido ${pedido.id}:`, e);
          }
        }
        
        if (itens.length > 0) {
          itens.forEach((item, index) => {
            // Tentar diferentes formas de acessar os dados do produto
            const nomeProduto = item.product?.nome || 
                               item.nome || 
                               item.produto?.nome || 
                               item.produto_nome ||
                               'Produto não encontrado';
            
            const descricaoProduto = item.product?.descricao || 
                                   item.descricao || 
                                   item.produto?.descricao || 
                                   item.descricao_produto ||
                                   'Não informado';
            
            const quantidade = item.quantidade || item.qtd || 0;
            const precoUnitario = item.preco_unitario || item.preco || item.valor_unitario;
            const subtotal = item.subtotal || item.valor_total || (quantidade * precoUnitario);
            const tamanho = item.tamanho || item.size || 'Não informado';
            
            itensDetalhados.push({
              'ID do Pedido': pedido.id,
              'Cliente': pedido.user?.username || pedido.dados_pessoais?.nome_completo || 'Não informado',
              'Status do Pedido': getStatusVisual(pedido.status),
              'Data do Pedido': formatDate(pedido.created_at),
              'Item': index + 1,
              'Nome do Produto': nomeProduto,
              'Descrição do Produto': descricaoProduto,
              'Tamanho': tamanho,
              'Quantidade': quantidade,
              'Preço Unitário': precoUnitario ? formatCurrency(precoUnitario) : 'Não informado',
              'Subtotal': subtotal ? formatCurrency(subtotal) : 'Não informado',
              'Total do Pedido': formatCurrency(pedido.total),
              'Logo Extra - Produtos': pedido.logo_extra?.produtos || pedido.logo_extra_produtos || 'Não solicitado',
              'Logo Extra - Entende Alteração de Valor': pedido.logo_extra?.entende_alteracao_valor ? 'Sim' : 'Não'
            });
          });
        } else {
          itensDetalhados.push({
            'ID do Pedido': pedido.id,
            'Cliente': pedido.user?.username || pedido.dados_pessoais?.nome_completo || 'Não informado',
            'Status do Pedido': getStatusVisual(pedido.status),
            'Data do Pedido': formatDate(pedido.created_at),
            'Item': 'N/A',
            'Nome do Produto': 'Nenhum item encontrado',
            'Descrição do Produto': 'N/A',
            'Tamanho': 'N/A',
            'Quantidade': 0,
            'Preço Unitário': 'N/A',
            'Subtotal': 'N/A',
            'Total do Pedido': formatCurrency(pedido.total),
            'Logo Extra - Produtos': pedido.logo_extra?.produtos || pedido.logo_extra_produtos || 'Não solicitado',
            'Logo Extra - Entende Alteração de Valor': pedido.logo_extra?.entende_alteracao_valor ? 'Sim' : 'Não'
          });
        }
      });
      
      // Criar workbook
      const workbook = XLSX.utils.book_new();

      // Aba 1: Resumo dos Pedidos
      const worksheetResumo = XLSX.utils.json_to_sheet(dadosParaExportar);
      
      // Ajustar largura das colunas do resumo
      const colWidthsResumo = [];
      dadosParaExportar.forEach(row => {
        Object.keys(row).forEach((key, index) => {
          if (!colWidthsResumo[index]) colWidthsResumo[index] = 0;
          colWidthsResumo[index] = Math.max(colWidthsResumo[index], key.length, String(row[key]).length);
        });
      });
      worksheetResumo['!cols'] = colWidthsResumo.map(width => ({ width: Math.min(width + 2, 50) }));

      // Aba 2: Itens Detalhados
      const worksheetItens = XLSX.utils.json_to_sheet(itensDetalhados);
      
      // Ajustar largura das colunas dos itens
      const colWidthsItens = [];
      itensDetalhados.forEach(row => {
        Object.keys(row).forEach((key, index) => {
          if (!colWidthsItens[index]) colWidthsItens[index] = 0;
          colWidthsItens[index] = Math.max(colWidthsItens[index], key.length, String(row[key]).length);
        });
      });
      worksheetItens['!cols'] = colWidthsItens.map(width => ({ width: Math.min(width + 2, 50) }));

      // Adicionar worksheets ao workbook
      XLSX.utils.book_append_sheet(workbook, worksheetResumo, 'Resumo dos Pedidos');
      XLSX.utils.book_append_sheet(workbook, worksheetItens, 'Itens Detalhados');

      // Gerar nome do arquivo com data atual
      const dataAtual = new Date().toISOString().split('T')[0];
      const nomeArquivo = `pedidos_exportados_${dataAtual}.xlsx`;

      // Salvar arquivo
      XLSX.writeFile(workbook, nomeArquivo);

      notification.success({
        message: 'Exportação Concluída',
        description: (
          <Space direction="vertical" size="small">
            <p><strong>Arquivo:</strong> {nomeArquivo}</p>
            <p><strong>Pedidos exportados:</strong> {pedidosFiltrados.length}</p>
            <p><strong>Conteúdo do arquivo:</strong></p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Aba 1: Resumo dos Pedidos (incluindo logo extra)</li>
              <li>Aba 2: Itens Detalhados com valores unitários e logo extra</li>
            </ul>
          </Space>
        ),
        duration: 8,
        placement: 'topRight'
      });
    } catch (error) {
      console.error('Erro ao exportar para Excel:', error);
      message.error('Erro ao exportar para Excel. Verifique o console para mais detalhes.');
    } finally {
      setExportando(false);
    }
  };


  // Configuração das colunas da tabela
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => `#${id}`,
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) => (
        <Space>
          <UserOutlined style={{ color: '#1890ff' }} />
          <span>{record.user?.username || 'Não informado'}</span>
        </Space>
      ),
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString('pt-BR'),
    },
    {
      title: 'Status do Pedido',
      dataIndex: 'status',
      key: 'orderStatus',
      render: (status) => {
        const statusConfig = {
          'Pendente de Aprovação': { color: 'orange', text: 'Pendente' },
          'Aprovado': { color: 'green', text: 'Aprovado' },
          'Em Preparação': { color: 'blue', text: 'Em Transporte' },
          'Em Transporte': { color: 'blue', text: 'Em Transporte' },
          'Enviado': { color: 'blue', text: 'Em Transporte' },
          'Entregue': { color: 'green', text: 'Entregue' },
          'Cancelado': { color: 'red', text: 'Cancelado' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (total) => formatCurrency(total),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button 
            type="text" 
            icon={<EyeOutlined />}
            onClick={() => handleViewPedido(record.id)}
          >
            Ver
          </Button>
          {record.status !== 'Entregue' && (
            <Select
              value={getStatusVisual(record.status)}
              onChange={(value) => handleStatusChange(record.id, value)}
              disabled={updatingStatus}
              style={{ width: 140 }}
              size="small"
            >
              <Select.Option value="Pendente de Aprovação">Pendente</Select.Option>
              <Select.Option value="Aprovado">Aprovado</Select.Option>
              <Select.Option value="Em Transporte">Em Transporte</Select.Option>
              <Select.Option value="Entregue">Entregue</Select.Option>
              <Select.Option value="Cancelado">Cancelado</Select.Option>
            </Select>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                Gerenciamento de Pedidos
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Visualize e gerencie todos os pedidos da loja
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Estatísticas */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Total de Pedidos" 
              value={pedidos.length} 
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Pendentes" 
              value={pedidos.filter(p => p.status === 'Pendente de Aprovação').length}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Aprovados" 
              value={pedidos.filter(p => p.status === 'Aprovado').length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Em Transporte" 
              value={pedidos.filter(p => p.status === 'Em Preparação' || p.status === 'Em Transporte' || p.status === 'Enviado').length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Entregues" 
              value={pedidos.filter(p => p.status === 'Entregue').length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={4}>
          <Card>
            <Statistic 
              title="Cancelados" 
              value={pedidos.filter(p => p.status === 'Cancelado').length}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card 
        style={{ marginBottom: 24 }}
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca - Administração
          </Space>
        }
        extra={
          <Space>
            <Button 
              type="text" 
              icon={<ReloadOutlined />}
              onClick={limparFiltros}
              size="large"
            >
              Limpar
            </Button>
            <Button 
              type="text" 
              icon={<FilterOutlined />}
              onClick={toggleFiltrosAvançados}
              size="large"
            >
              {filtrosAvançadosAbertos ? 'Ocultar' : 'Mostrar'} Filtros Avançados
            </Button>
          </Space>
        }
      >
         {/* Filtros Básicos */}
         <div className="filters-container">
           <div className="filter-row">
             <div className="filter-group">
               <label className="filter-label">ID do Pedido</label>
               <input
                 type="text"
                 className="filter-input"
                 placeholder="Ex: 123"
                 value={valoresFiltros.id || ''}
                 onChange={(e) => setValoresFiltros({...valoresFiltros, id: e.target.value})}
               />
             </div>
             <div className="filter-group">
               <label className="filter-label">Nome do Cliente</label>
               <input
                 type="text"
                 className="filter-input"
                 placeholder="Ex: João Silva"
                 value={valoresFiltros.cliente || ''}
                 onChange={(e) => setValoresFiltros({...valoresFiltros, cliente: e.target.value})}
               />
             </div>
             <div className="filter-group">
               <label className="filter-label">Status do Pedido</label>
               <div className="custom-dropdown-container">
                 <div 
                   className="custom-dropdown-trigger"
                   onClick={() => setDropdownOpen(!dropdownOpen)}
                 >
                   <span className="dropdown-text">
                     {valoresFiltros.status || 'Selecione o status'}
                   </span>
                   <span className="dropdown-arrow">
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                       <polyline points="6,9 12,15 18,9"></polyline>
                     </svg>
                   </span>
                 </div>
                 {dropdownOpen && (
                   <div className="custom-dropdown-menu">
                     <div 
                       className={`dropdown-option ${!valoresFiltros.status ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: ''});
                         setDropdownOpen(false);
                       }}
                     >
                       Selecione o status
                     </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Pendente de Aprovação' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Pendente de Aprovação'});
                         setDropdownOpen(false);
                       }}
                     >
                       Pendente de Aprovação
                     </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Aprovado' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Aprovado'});
                         setDropdownOpen(false);
                       }}
                     >
                       Aprovado
                     </div>
                    <div 
                      className={`dropdown-option ${valoresFiltros.status === 'Em Transporte' ? 'selected' : ''}`}
                      onClick={() => {
                        setValoresFiltros({...valoresFiltros, status: 'Em Transporte'});
                        setDropdownOpen(false);
                      }}
                    >
                      Em Transporte
                    </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Entregue' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Entregue'});
                         setDropdownOpen(false);
                       }}
                     >
                       Entregue
                     </div>
                     <div 
                       className={`dropdown-option ${valoresFiltros.status === 'Cancelado' ? 'selected' : ''}`}
                       onClick={() => {
                         setValoresFiltros({...valoresFiltros, status: 'Cancelado'});
                         setDropdownOpen(false);
                       }}
                     >
                       Cancelado
                     </div>
                   </div>
                 )}
               </div>
             </div>
             <div className="filter-group">
               <div style={{ display: 'flex', gap: '8px' }}>
                 <button 
                   type="button"
                   className="filter-button"
                   onClick={aplicarFiltros}
                 >
                   <SearchOutlined />
                   Buscar
                 </button>
                 <button 
                   type="button"
                   className="filter-button export-button"
                   onClick={exportarParaExcel}
                   disabled={exportando}
                 >
                   <ExportOutlined />
                   {exportando ? 'Exportando...' : `Exportar (${pedidosFiltrados.length})`}
                 </button>
               </div>
             </div>
           </div>

           {/* Filtros Avançados */}
           <div className="advanced-filters">
             <button 
               className="toggle-advanced-btn"
               onClick={toggleFiltrosAvançados}
             >
               <CalendarOutlined />
               {filtrosAvançadosAbertos ? 'Ocultar' : 'Mostrar'} Filtros Avançados
             </button>
             
             {filtrosAvançadosAbertos && (
               <div className="advanced-filters-content">
                 <div className="filter-row">
                   <div className="filter-group">
                     <label className="filter-label">ID Inicial</label>
                     <input
                       type="number"
                       className="filter-input"
                       placeholder="Ex: 100"
                       value={valoresFiltros.idInicial || ''}
                       onChange={(e) => setValoresFiltros({...valoresFiltros, idInicial: e.target.value})}
                     />
                   </div>
                   <div className="filter-group">
                     <label className="filter-label">ID Final</label>
                     <input
                       type="number"
                       className="filter-input"
                       placeholder="Ex: 200"
                       value={valoresFiltros.idFinal || ''}
                       onChange={(e) => setValoresFiltros({...valoresFiltros, idFinal: e.target.value})}
                     />
                   </div>
                   <div className="filter-group">
                     <label className="filter-label">Data Início</label>
                     <DatePicker
                       placeholder="Selecione a data"
                       value={valoresFiltros.dataInicio}
                       onChange={(date) => setValoresFiltros({...valoresFiltros, dataInicio: date})}
                       style={{
                         width: '100%',
                         height: '40px'
                       }}
                       format="DD/MM/YYYY"
                     />
                   </div>
                   <div className="filter-group">
                     <label className="filter-label">Data Fim</label>
                     <DatePicker
                       placeholder="Selecione a data"
                       value={valoresFiltros.dataFim}
                       onChange={(date) => setValoresFiltros({...valoresFiltros, dataFim: date})}
                       style={{
                         width: '100%',
                         height: '40px'
                       }}
                       format="DD/MM/YYYY"
                     />
                   </div>
                 </div>
               </div>
             )}
           </div>
         </div>
        
        <div style={{ marginTop: 10, textAlign: 'center', color: '#666' }}>
          <Space>
            <span>Mostrando <strong>{pedidosFiltrados.length}</strong> de <strong>{pedidos.length}</strong> pedidos</span>
            {Object.keys(filtrosAplicados).length > 0 && (
              <Tag color="blue">
                {Object.keys(filtrosAplicados).length} filtro(s) aplicado(s)
              </Tag>
            )}
          </Space>
        </div>
      </Card>

      {/* Tabela */}
      <Card>
        <Table
          columns={columns}
          dataSource={pedidosFiltrados}
          rowKey="id"
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} pedidos`,
            onShowSizeChange: (current, size) => setPageSize(size),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* Modal de Detalhes do Pedido */}
      <Modal
        title={`Pedido #${selectedPedido?.id}`}
        open={showModal}
        onCancel={() => setShowModal(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
        ]}
      >
        {selectedPedido && (
          <div>
            {/* Informações do Pedido */}
            <Card title={<><FileTextOutlined /> Informações do Pedido</>} style={{ marginBottom: 16 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(selectedPedido.status)}>
                      {getStatusVisual(selectedPedido.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Data">
                  {formatDate(selectedPedido.created_at)}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  {formatCurrency(selectedPedido.total)}
                </Descriptions.Item>
                  {selectedPedido.observacoes && (
                  <Descriptions.Item label="Observações" span={2}>
                    {selectedPedido.observacoes}
                  </Descriptions.Item>
                  )}
                  {selectedPedido.status === 'Cancelado' && selectedPedido.motivo_cancelamento && (
                  <Descriptions.Item label="Motivo do Cancelamento" span={2}>
                    <Tag color="red">{selectedPedido.motivo_cancelamento}</Tag>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Dados do Cliente */}
            <Card title={<><UserOutlined /> Dados do Cliente</>} style={{ marginBottom: 16 }}>
              <Descriptions column={2}>
                <Descriptions.Item label="Nome">
                  {selectedPedido.dados_pessoais?.nome_completo || selectedPedido.user?.username || selectedPedido.cliente_nome || 'Não informado'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {selectedPedido.user?.email || selectedPedido.cliente_email || 'Não informado'}
                </Descriptions.Item>
                  {selectedPedido.dados_pessoais?.cpf && (
                  <Descriptions.Item label="CPF">
                    {selectedPedido.dados_pessoais.cpf}
                  </Descriptions.Item>
                  )}
                  {selectedPedido.dados_pessoais?.nome_empresa && (
                  <Descriptions.Item label="Empresa">
                    {selectedPedido.dados_pessoais.nome_empresa}
                  </Descriptions.Item>
                  )}
                  {selectedPedido.dados_pessoais?.cnpj && (
                  <Descriptions.Item label="CNPJ">
                    {selectedPedido.dados_pessoais.cnpj}
                  </Descriptions.Item>
                  )}
                  {selectedPedido.dados_pessoais?.telefone && (
                  <Descriptions.Item label="Telefone">
                    {selectedPedido.dados_pessoais.telefone}
                  </Descriptions.Item>
                  )}
              </Descriptions>
            </Card>

            {/* Dados de Entrega */}
                {selectedPedido.dados_entrega && (
              <Card title={<><TruckOutlined /> Dados de Entrega</>} style={{ marginBottom: 16 }}>
                <Descriptions column={2}>
                    {selectedPedido.dados_entrega.quem_recebera && (
                    <Descriptions.Item label="Quem receberá">
                      {selectedPedido.dados_entrega.quem_recebera}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Rua">
                    {selectedPedido.dados_entrega.rua}
                  </Descriptions.Item>
                  <Descriptions.Item label="Número">
                    {selectedPedido.dados_entrega.numero}
                  </Descriptions.Item>
                    {selectedPedido.dados_entrega.complemento && (
                    <Descriptions.Item label="Complemento">
                      {selectedPedido.dados_entrega.complemento}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Bairro">
                    {selectedPedido.dados_entrega.bairro}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cidade">
                    {selectedPedido.dados_entrega.cidade}
                  </Descriptions.Item>
                  <Descriptions.Item label="Estado">
                    {selectedPedido.dados_entrega.estado}
                  </Descriptions.Item>
                  <Descriptions.Item label="CEP">
                    {selectedPedido.dados_entrega.cep}
                  </Descriptions.Item>
                    {selectedPedido.dados_entrega.ponto_referencia && (
                    <Descriptions.Item label="Ponto de Referência" span={2}>
                      {selectedPedido.dados_entrega.ponto_referencia}
                    </Descriptions.Item>
                    )}
                </Descriptions>
              </Card>
                )}

            {/* Dados de Faturamento */}
                {selectedPedido.dados_faturamento && !selectedPedido.endereco_entrega_igual_faturamento && (
              <Card title={<><CreditCardOutlined /> Dados de Faturamento</>} style={{ marginBottom: 16 }}>
                <Descriptions column={2}>
                  <Descriptions.Item label="Rua">
                    {selectedPedido.dados_faturamento.rua}
                  </Descriptions.Item>
                  <Descriptions.Item label="Número">
                    {selectedPedido.dados_faturamento.numero}
                  </Descriptions.Item>
                    {selectedPedido.dados_faturamento.complemento && (
                    <Descriptions.Item label="Complemento">
                      {selectedPedido.dados_faturamento.complemento}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Bairro">
                    {selectedPedido.dados_faturamento.bairro}
                  </Descriptions.Item>
                  <Descriptions.Item label="Cidade">
                    {selectedPedido.dados_faturamento.cidade}
                  </Descriptions.Item>
                  <Descriptions.Item label="Estado">
                    {selectedPedido.dados_faturamento.estado}
                  </Descriptions.Item>
                  <Descriptions.Item label="CEP">
                    {selectedPedido.dados_faturamento.cep}
                  </Descriptions.Item>
                    {selectedPedido.dados_faturamento.ponto_referencia && (
                    <Descriptions.Item label="Ponto de Referência" span={2}>
                      {selectedPedido.dados_faturamento.ponto_referencia}
                    </Descriptions.Item>
                    )}
                </Descriptions>
              </Card>
                )}

                {selectedPedido.endereco_entrega_igual_faturamento && (
              <Card style={{ marginBottom: 16 }}>
                <Tag color="blue"><><InfoCircleOutlined /> Endereço de entrega é o mesmo do faturamento</></Tag>
              </Card>
            )}

            {/* Itens do Pedido */}
            <Card title={<><ShoppingOutlined /> Itens do Pedido</>} style={{ marginBottom: 16 }}>
              {(selectedPedido.itens && selectedPedido.itens.length > 0) || (selectedPedido.items && selectedPedido.items.length > 0) ? (
                <Table
                  dataSource={selectedPedido.itens || selectedPedido.items || []}
                  columns={[
                    {
                      title: 'Produto',
                      dataIndex: ['product', 'nome'],
                      key: 'produto',
                      render: (text, record) => text || record.nome || 'Produto não encontrado',
                    },
                    {
                      title: 'Quantidade',
                      dataIndex: 'quantidade',
                      key: 'quantidade',
                    },
                    {
                      title: 'Tamanho',
                      dataIndex: 'tamanho',
                      key: 'tamanho',
                      render: (tamanho) => tamanho ? <Tag>{tamanho}</Tag> : '-',
                    },
                    {
                      title: 'Preço Unit.',
                      dataIndex: 'preco_unitario',
                      key: 'preco_unitario',
                      render: (preco) => formatCurrency(preco),
                    },
                    {
                      title: 'Subtotal',
                      dataIndex: 'subtotal',
                      key: 'subtotal',
                      render: (subtotal) => formatCurrency(subtotal),
                    },
                  ]}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                />
              ) : (
                <p>Nenhum item encontrado para este pedido.</p>
              )}
            </Card>

            {/* Logo Extra */}
                {((selectedPedido.logo_extra && selectedPedido.logo_extra.produtos) || 
                  (selectedPedido.logo_extra_produtos && selectedPedido.logo_extra_produtos.trim())) && (
              <Card title="Logo Extra">
                <div>
                      <h4>Produtos para Logo Extra:</h4>
                        <p>{selectedPedido.logo_extra?.produtos || selectedPedido.logo_extra_produtos}</p>
                      </div>
              </Card>
                )}

            {/* Alterar Status */}
                {selectedPedido.status !== 'Entregue' && (
              <Card title={<><SettingOutlined /> Alterar Status</>} style={{ marginTop: 16 }}>
                <Space>
                  <Select
                        value={getStatusVisual(selectedPedido.status)}
                    onChange={(value) => handleStatusChange(selectedPedido.id, value)}
                        disabled={updatingStatus}
                    style={{ width: 200 }}
                  >
                    <Select.Option value="Pendente de Aprovação">Pendente de Aprovação</Select.Option>
                    <Select.Option value="Aprovado">Aprovado</Select.Option>
                    <Select.Option value="Em Transporte">Em Transporte</Select.Option>
                    <Select.Option value="Entregue">Entregue</Select.Option>
                    <Select.Option value="Cancelado">Cancelado</Select.Option>
                  </Select>
                  {updatingStatus && <span>Atualizando...</span>}
                </Space>
              </Card>
                    )}
        </div>
      )}
      </Modal>

      {/* Modal de Cancelamento */}
      <Modal
        title={`Cancelar Pedido #${pedidoParaCancelar?.id}`}
        open={showCancelModal}
        onCancel={() => setShowCancelModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowCancelModal(false)}>
            Cancelar
          </Button>,
          <Button 
            key="confirm" 
            type="primary" 
            danger
            onClick={handleCancelarPedido}
            disabled={!motivoCancelamento.trim()}
          >
            Confirmar Cancelamento
          </Button>
        ]}
      >
        {pedidoParaCancelar && (
          <div>
            <Descriptions column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Cliente">
                {pedidoParaCancelar.user?.username || 'Não informado'}
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                {formatCurrency(pedidoParaCancelar.total)}
              </Descriptions.Item>
              <Descriptions.Item label="Data">
                {formatDate(pedidoParaCancelar.created_at)}
              </Descriptions.Item>
            </Descriptions>
            
            <div>
              <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>
                Motivo do Cancelamento:
                  </label>
              <Input.TextArea
                    value={motivoCancelamento}
                    onChange={(e) => setMotivoCancelamento(e.target.value)}
                    placeholder="Digite o motivo do cancelamento..."
                rows={4}
              />
          </div>
        </div>
      )}
      </Modal>
    </Space>
  );
};

export default AdminPedidos; 
